import { IconWebhook } from '@developerz/ui';
import { A } from '@solidjs/router';
import type { JSX } from 'solid-js';
import { For, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { useRepo, useRepoSettings } from '../lib/repo-detail';
import { useMe } from '../lib/session';
import { createToast } from '../lib/toast';
import { canManageWebhooks } from '../lib/webhooks';
import { createWebhookTest } from '../lib/webhookTest';
import { AgentModesPanel } from './AgentModesPanel';
import { EmptyState } from './EmptyState';
import { OverrideRow } from './OverrideRow';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { RepoDonationsPanel } from './RepoDonationsPanel';
import { RepoEnableSwitchPanel } from './RepoEnableSwitchPanel';
import type { RepoTabProps } from './RepoOverviewTab';
import { RepoPromptCard } from './RepoPromptCard';
import { RepoSetupPanel } from './RepoSetupPanel';
import { Skeleton } from './Skeleton';
import { Toast } from './Toast';
import { buttonClass } from './ui/Button';
import { WebhookRow } from './WebhookRow';

// Repo detail → Settings (#195): the repo's two owner switches + prompt card +
// the per-repo override flags + the webhook binding the repo's policy hands off to.
//
// Two different kinds of thing, deliberately shown differently:
//   • Overrides are DERIVED from the repo's own `.maintainer.yml` (the file is
//     the source of truth, the dashboard never writes it) — so they render as
//     read-only facts with their source, not as switches nothing can flip.
//   • Real writes are wired, busy-guarded and report what the attempt did: the
//     two switches (`PATCH /v1/repos/:owner/:repo` — enablement #972, donations
//     arc 101 slice 02, `write:repos`), the setup dispatch (#1728, `write:tasks`)
//     and the test-send (`POST /v1/webhooks/:id/test`, `write:webhooks`, owner-
//     only). Creating webhooks is an ACCOUNT action; the panel links to its page.

export function RepoSettingsTab(props: RepoTabProps): JSX.Element {
  const settings = useRepoSettings(() => props.encoded, props.token);
  const repo = useRepo(() => props.encoded, props.token);
  const me = useMe(props.token);
  const { toast, notify, dismiss } = createToast();
  const test = createWebhookTest({ token: props.token, notify });
  // Mirrors `write:webhooks` (owner-only) so a caller who would plainly 403
  // isn't handed a live button; the server stays the authority. Shut until
  // `/v1/me` lands: mid-load `role` is undefined, the same shape as the
  // PAT / machine credential that KEEPS the control, so the button would otherwise go
  // live before we know who is asking.
  const canTest = (): boolean => !me.isLoading && canManageWebhooks(me.data?.role);
  // Not `!canTest()` — mid-load the write isn't denied, it's unknown, and
  // "owner action" would be a guess that flips a moment later.
  const denied = (): boolean => !me.isLoading && !canManageWebhooks(me.data?.role);

  return (
    <QueryBoundary query={settings} loading={<Skeleton height="16rem" />}>
      {(s) => (
        <div class="repo-detail__stack">
          {/* Enablement (#972), the donations opt-in (arc 101 slice 02) + the
              REPO layer of the system-prompt cascade (#637). All three need the
              identity row, so they mount once it lands. The donations panel
              re-reads that row rather than navigating away: unlike a disable,
              the flip leaves this workspace exactly where it was. A QueryBoundary,
              not a bare Show: a failed identity read painted nothing with no
              Retry (plan 101 slice 17 row 17-13). */}
          <QueryBoundary query={repo} loading={<Skeleton height="10rem" />}>
            {(r) => (
              <>
                <RepoEnableSwitchPanel repo={r} token={props.token} />
                <RepoDonationsPanel
                  repo={r}
                  token={props.token}
                  onSaved={() => void repo.refetch()}
                />
                <RepoPromptCard repoId={r.id} token={props.token} />
                {/* Plan 101 slice 06 — this repo's override of the org's modes. */}
                <AgentModesPanel scope={{ kind: 'repo', repoId: r.id }} token={props.token} />
                {/* #1728 — `write:tasks`, so NOT behind the owner mirror above. */}
                <RepoSetupPanel fullName={r.full_name} token={props.token} />
              </>
            )}
          </QueryBoundary>

          <Panel
            title={t('repo_detail.settings_overrides')}
            footer={t('repo_detail.settings_overrides_hint')}
            class="card--footered"
            action={
              <A
                class={buttonClass({ variant: 'secondary', size: 'sm' })}
                href={orgHref(`/repos/${props.encoded}/policy`)}
              >
                {t('repo_detail.settings_view_policy')}
              </A>
            }
          >
            <ul class="repo-settings__list">
              <OverrideRow
                label={t('repo_detail.settings_override_global')}
                on={s.overrides.override_global_rules}
                source={t('repo_detail.settings_source_policy_file')}
              />
              <OverrideRow
                label={t('repo_detail.settings_strict_linting')}
                on={s.overrides.strict_linting}
                source={t('repo_detail.settings_source_required_checks')}
              />
            </ul>
          </Panel>

          <Panel
            title={t('repo_detail.settings_webhook_bindings')}
            footer={t('repo_detail.settings_bindings_hint')}
            class="card--footered"
            action={
              <A
                class={buttonClass({ variant: 'secondary', size: 'sm' })}
                href={orgHref('/account/webhooks')}
              >
                <IconWebhook />
                {t('repo_detail.settings_manage_webhooks')}
              </A>
            }
          >
            <Show
              when={s.webhooks.length > 0}
              fallback={<EmptyState message={t('repo_detail.settings_bindings_empty')} />}
            >
              <ul class="repo-settings__list">
                <For each={s.webhooks}>
                  {(h) => (
                    <WebhookRow
                      hook={h}
                      testing={test.isTesting(h.id)}
                      {...(canTest() ? { onTest: test.run } : {})}
                    />
                  )}
                </For>
              </ul>
            </Show>
            {/* A dead button with no reason reads as a bug; say why it's dead. */}
            <Show when={s.webhooks.length > 0 && denied()}>
              <p class="repo-settings__note" role="status">
                {t('repo_detail.settings_test_denied')}
              </p>
            </Show>
          </Panel>

          <Toast
            message={toast()?.message ?? null}
            tone={toast()?.tone ?? 'success'}
            onDismiss={dismiss}
          />
        </div>
      )}
    </QueryBoundary>
  );
}
